import {
    AppBar,
    Toolbar,
    Typography,
    Box,
    Chip,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Avatar,
    Menu,
    MenuItem,
    Divider,
    Drawer,
    CssBaseline,
    Collapse,
    Tooltip,
    useTheme,
} from '@mui/material';
import {
    Business,
    Menu as MenuIcon,
    Dashboard,
    Assignment,
    AccountBalance,
    // TrendingUp,  // Hidden for now (Sales module)
    Description,
    People,
    Note as NoteIcon,
    Logout,
    UploadFile,
    Settings,
    ExpandLess,
    ExpandMore,
    ChevronLeft,
    ChevronRight,
    Summarize,
    ReceiptLong,
    TrendingUp,
    SmartToy,
   AutoAwesome
} from '@mui/icons-material';
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useAuth } from '../../context/AuthContext';
import { removeAuthToken } from '../../api/client';
import NotesPanel, { NOTES_PANEL_WIDTH } from '../common/NotesPanel';
import { useModulePermission } from '../../utils/useModulePermission';
import { CalendarIcon } from '@mui/x-date-pickers';

export const DRAWER_WIDTH = 280;
export const COLLAPSED_DRAWER_WIDTH = 88; // Width for icons-only state

export interface SidebarProps {
    onMenuClick?: () => void;
    mobileOpen?: boolean;
    onDrawerToggle?: () => void;
    pageTitle?: string;
    pageActions?: React.ReactNode;
    isCollapsed?: boolean;
    onCollapseToggle?: () => void;
    isNotesOpen?: boolean;
    onNotesOpenChange?: (open: boolean) => void;
    startDate?: string;
    endDate?: string;
}

type NavItem = {
    label: string;
    icon: any; // or React.ElementType
    path?: string;
    children?: { label: string; path: string; icon?: any }[];
};

/** Child label → backend permission module_name (for filtering Delivery children) */
const CHILD_LABEL_TO_PERMISSION: Record<string, string> = {
    'Delivery Summary': 'Dashboard',
    'Project details': 'Projects',
    'Allocations': 'Allocations',
    'Finance overview': 'Finance',
    'Purchase Orders': 'Purchase Orders',
    'Invoices': 'Purchase Orders',
    'Invoice Calendar': 'Invoice Calendar',
    'Conversion Rate': 'Conversion Rate',
};

const navigationItems: NavItem[] = [
    { label: 'Executive Summary', path: '/', icon: Summarize },
    // { label: 'Projects', path: '/projects/details', icon: Business },
    {
        label: 'Delivery',
        icon: Business,
        children: [
            { label: 'Delivery Summary', path: '/delivery-summary', icon: Summarize },
            { label: 'Project details', path: '/projects/details' },
            { label: 'Allocations', path: '/allocations', icon: Assignment },

            // { label: 'Gross margin', path: '/projects/gm-summary' },  // Hidden for now
        ],
    },
    {
        label: 'Finance',
        icon: AccountBalance,
        children: [
            { label: 'Finance overview', path: '/finance', icon: AccountBalance },
            { label: 'Purchase Orders', path: '/purchase-orders', icon: Description },
            { label: 'Invoices', path: '/invoices', icon: ReceiptLong },
            { label: 'Invoice Calendar', path: '/invoice-calendar', icon: CalendarIcon },
            { label: 'Conversion Rate', path: '/conversion-rate', icon: TrendingUp },

        ],
    },
    // { label: 'Sales', path: '/sales', icon: TrendingUp },  // Hidden for now
    // { label: 'Purchase Orders', path: '/purchase-orders', icon: Description },
    { label: 'Po Analysis', path: '/po-analysis', icon: Description },
    { label: 'HR Analysis', path: '/hr-analysis', icon: People },
    { label: 'AI Agent', path: '/agentic-ai', icon: AutoAwesome },
    // { label: 'Leave Balance', path: '/leave-balance', icon: People },

    // { label: 'Project and Simulation', path: '/projection-simulation', icon: Analytics },
    { label: 'Uploads', path: '/uploads', icon: UploadFile },
    { label: 'Settings', path: '/settings', icon: Settings },

];

const Sidebar: React.FC<SidebarProps> = ({
    onMenuClick,
    mobileOpen = false,
    onDrawerToggle,
    pageTitle,
    pageActions,
    isCollapsed = false,
    onCollapseToggle,
    isNotesOpen = false,
    onNotesOpenChange,
    startDate = '',
    endDate = '',
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { instance, accounts } = useMsal();
    const { user, setUser } = useAuth();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const currentUser = accounts[0];
    const userName = currentUser?.name || currentUser?.username || 'User';
    const userEmail = currentUser?.username || '';
    const userInitials = userName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

    const [visibleNavigationItems, setVisibleNavigationItems] = useState<NavItem[]>([]);
    const [openSubmenus, setOpenSubmenus] = useState<Record<string, boolean>>({});
    const [notesView, setNotesView] = useState<'list' | 'form' | 'comments'>('list');

    const currentModuleName = (() => {
        const path = location.pathname;
        if (path === '/') return 'Dashboard';
        if (path.startsWith('/projects')) return 'Projects';
        if (path.startsWith('/allocations')) return 'Allocations';
        if (path.startsWith('/finance')) return 'Finance';
        if (path.startsWith('/invoice-calendar')) return 'Invoice Calendar';
        if (path.startsWith('/conversion-rate')) return 'Conversion Rate';
        if (path.startsWith('/sales')) return 'Sales';
        if (path.startsWith('/purchase-orders')) return 'Purchase Orders';
        if (path.startsWith('/invoices')) return 'Purchase Orders';
        if (path.startsWith('/po-analysis')) return 'Po Analysis';
        if (path.startsWith('/hr-analysis')) return 'HR Analysis';
        if (path.startsWith('/uploads')) return 'Uploads';
        if (path.startsWith('/settings')) return 'Settings';
        if (path.startsWith('/agentic-ai')) return 'AI Agent';
        return 'Dashboard';
    })();

    const notesPage = (() => {
        const path = location.pathname;
        if (path === '/') return 'dashboard';
        return path.split('/').filter(Boolean)[0] || 'dashboard';
    })();

    const { can } = useModulePermission(currentModuleName);
    const notesPermissions = {
        canCreate: can('write'),
        canUpdate: can('update'),
        canDelete: can('delete'),
        canUpload: can('upload'),
    };

    const searchParams = new URLSearchParams(location.search);
    const notesRequested = searchParams.get('notes') === '1';
    const notesStatusParam = searchParams.get('status');
    const notesStatusFilter: '' | 'open' | 'closed' =
        notesStatusParam === 'open' ? 'open' : notesStatusParam === 'closed' ? 'closed' : '';

    const hasDateRange = Boolean(startDate && endDate);
    const actionItemsRangeLabel = hasDateRange
        ? `${dayjs(`${startDate}-01`).format('MMM YYYY')} - ${dayjs(`${endDate}-01`).format('MMM YYYY')}`
        : 'selected range';

    const showActionItemChips = Boolean(isNotesOpen && notesView === 'list' && !isCollapsed);

    const openActionItemsWithStatus = (status: '' | 'open' | 'closed') => {
        if (isCollapsed && onCollapseToggle) onCollapseToggle();
        if (!startDate || !endDate) return;

        const startFull = dayjs(`${startDate}-01`).startOf('month').format('YYYY-MM-DD');
        const endFull = dayjs(`${endDate}-01`).endOf('month').format('YYYY-MM-DD');

        const params = new URLSearchParams(location.search);
        params.set('notes', '1');
        params.set('module', 'delivery');
        params.set('page', notesPage);
        params.set('start_date', startFull);
        params.set('end_date', endFull);

        if (status) params.set('status', status);
        else params.delete('status');

        onNotesOpenChange?.(true);
        navigate({ pathname: location.pathname, search: `?${params.toString()}` });
    };

    const openNotes = () => {
        if (isCollapsed && onCollapseToggle) onCollapseToggle();
        if (!startDate || !endDate) {
            onNotesOpenChange?.(true);
            return;
        }
        openActionItemsWithStatus('');
    };

    const clearNotesQuery = () => {
        const next = new URLSearchParams(location.search);
        next.delete('notes');
        next.delete('status');
        next.delete('module');
        next.delete('page');
        next.delete('start_date');
        next.delete('end_date');
        const nextSearch = next.toString();
        navigate({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' }, { replace: true });
    };

    const closeNotes = () => {
        setNotesView('list');
        onNotesOpenChange?.(false);
        if (notesRequested) clearNotesQuery();
    };

    useEffect(() => {
        if (!notesRequested) return;
        if (isCollapsed && onCollapseToggle) onCollapseToggle();
        onNotesOpenChange?.(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notesRequested]);

    useEffect(() => {
        if (user) {
            const permissions = (user as any)?.role?.permissions || [];
            let allowedModules = permissions
                .filter((p: any) => p?.permissions?.read === true)
                .map((p: any) => p?.module_name);

            // Dashboard → Delivery Summary (sidebar label)
            const hasDashboard = permissions.some((p: any) => p?.module_name === 'Dashboard' && p?.permissions?.read === true);
            if (!hasDashboard) {
                allowedModules = allowedModules.filter((m: any) => m !== 'Delivery Summary');
            } else if (!allowedModules.includes('Delivery Summary')) {
                allowedModules.push('Delivery Summary');

            }
            // Executive Dashboard → Executive Summary (sidebar label)
            const hasExecutiveDashboard = permissions.some((p: any) => p?.module_name === 'Executive Dashboard' && p?.permissions?.read === true);
            if (!hasExecutiveDashboard) {
                allowedModules = allowedModules.filter((m: any) => m !== 'Executive Summary');
            } else if (!allowedModules.includes('Executive Summary')) {
                allowedModules.push('Executive Summary');
            }
            // Leave Balance
            const hasLeaveBalance = permissions.some((p: any) => p?.module_name === 'Leave Balance' && p?.permissions?.read === true);
            if (!hasLeaveBalance) {
                allowedModules = allowedModules.filter((m: any) => m !== 'Leave Balance');
            } else if (!allowedModules.includes('Leave Balance')) {
                allowedModules.push('Leave Balance');
            }

            const hasReadPermission = (moduleName: string) =>
                permissions.some((p: any) => p?.module_name === moduleName && p?.permissions?.read === true);

            const filteredItems: NavItem[] = navigationItems
                .map((item) => {
                    if (item.children && item.children.length > 0) {
                        const allowedChildren = item.children.filter((child) => {
                            const moduleName = CHILD_LABEL_TO_PERMISSION[child.label];
                            return moduleName ? hasReadPermission(moduleName) : false;
                        });
                        if (allowedChildren.length === 0) return null;
                        return { ...item, children: allowedChildren };
                    }
                    return allowedModules.includes(item.label) ? item : null;
                })
                .filter((item): item is NavItem => item != null);

            setVisibleNavigationItems(filteredItems);
        } else {
            // Fallback or default behavior if no user data
            setVisibleNavigationItems(navigationItems);
        }
    }, [user]);

    // Helper: root path '/' must match exactly so "Delivery Summary" is selected on '/' not on every route
    const isChildPathActive = (pathname: string, childPath: string) =>
        childPath === '/' ? pathname === '/' : pathname.startsWith(childPath);

    // Keep parent menus open when a child route is active
    useEffect(() => {
        if (isCollapsed) return; // Don't auto-expand usually if collapsed, but fine to keep state

        setOpenSubmenus((prev) => {
            const next = { ...prev };
            visibleNavigationItems.forEach((item) => {
                if (!item.children || item.children.length === 0) return;
                const isChildActive = item.children.some((c) => isChildPathActive(location.pathname, c.path));
                if (isChildActive) next[item.label] = true;
            });
            return next;
        });
    }, [location.pathname, visibleNavigationItems, isCollapsed]);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        handleMenuClose();
        removeAuthToken();
        setUser(null);
        instance.logoutRedirect().catch(e => console.error(e));
    };

    const handleItemClick = (item: NavItem) => {
        if (item.children && item.children.length > 0) {
            const firstChildPath = item.children[0].path;
            const pathToNavigate = item.path ?? firstChildPath;
            if (isCollapsed && onCollapseToggle) {
                // If collapsed and has children, expand sidebar first
                onCollapseToggle();
                setOpenSubmenus((prev) => ({ ...prev, [item.label]: true }));
                if (pathToNavigate) navigate(pathToNavigate);
            } else if (!openSubmenus[item.label]) {
                // Submenu is currently closed – open it and navigate to first child (or parent path)
                setOpenSubmenus((prev) => ({ ...prev, [item.label]: true }));
                if (pathToNavigate) navigate(pathToNavigate);
            } else {
                // Submenu is already open – just toggle it closed
                setOpenSubmenus((prev) => ({ ...prev, [item.label]: false }));
            }
        } else if (item.path) {
            navigate(item.path);
        }
    };

    const drawerContent = (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflowX: 'hidden', // Hide overflow during transition
                bgcolor: isCollapsed ? '#f1f5f9' : '#f1f5f9',
                transition: theme.transitions.create(['background-color'], {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.enteringScreen,
                }),
                // Custom scrollbar styles
                '&::-webkit-scrollbar': {
                    width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                    backgroundColor: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#cbd5e1',
                    borderRadius: '3px',
                    '&:hover': {
                        backgroundColor: '#94a3b8',
                    },
                },
                // Firefox scrollbar
                scrollbarWidth: 'thin',
                scrollbarColor: '#cbd5e1 transparent',
            }}
        >
            {/* Logo Section */}
            <Box sx={{
                p: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                minHeight: 80, // Keep height consistent
                transition: theme.transitions.create(['padding', 'justify-content'], {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.enteringScreen,
                }),
            }}>
                <Box
                    component="img"
                    src="/focaloid_logo_2.png"
                    alt="Focaloid"
                    sx={{
                        width: 40,
                        height: 40,
                        minWidth: 40, // Prevent shrinking
                        borderRadius: 2,
                        objectFit: 'contain',
                        backgroundColor: 'black',
                        boxShadow: '0 4px 6px -1px rgba(15, 23, 42, 0.12)',
                        p: 0.5,
                    }}
                />
                <Box sx={{
                    minWidth: 0,
                    opacity: isCollapsed ? 0 : 1,
                    width: isCollapsed ? 0 : 'auto',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    transition: theme.transitions.create(['opacity', 'width'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    })
                }}>
                    <Typography
                        variant="subtitle1"
                        noWrap
                        sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', lineHeight: 1.1 }}
                    >
                        Focaloid
                    </Typography>
                    <Typography
                        variant="caption"
                        noWrap
                        sx={{ color: '#64748b', display: 'block', lineHeight: 1.2 }}
                    >
                        Delivery Review
                    </Typography>
                </Box>
            </Box>


            {/* Toggle Button (Desktop Only) */}
            <Box sx={{
                px: 1,
                pb: 1,
                display: { xs: 'none', md: 'flex' },
                justifyContent: isCollapsed ? 'center' : 'flex-end',
            }}>
                {!isNotesOpen && (
                    <IconButton
                        onClick={() => {
                            // If user collapses the sidebar while Notes is open,
                            // close Notes first to keep the collapsed sidebar clean.
                            if (!isCollapsed && isNotesOpen) {
                                closeNotes();
                            }
                            onCollapseToggle?.();
                        }}
                        size="small"
                    >
                        {isCollapsed ? <ChevronRight /> : <ChevronLeft />}
                    </IconButton>
                )}
            </Box>

            {/* Notes trigger in header */}
            <Box sx={{ px: isCollapsed ? 1 : 2, pb: 1.5 }}>
                <Tooltip title={isCollapsed ? 'Action Items' : ''} placement="right" arrow>
                    <Box
                        role="button"
                        tabIndex={0}
                        onClick={openNotes}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') openNotes();
                        }}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: isCollapsed ? 'center' : 'flex-start',
                            gap: 1.25,
                            width: '100%',
                            px: 1.5,
                            py: 1.25,
                            borderRadius: 2,
                            cursor: 'pointer',
                            userSelect: 'none',
                            bgcolor: isNotesOpen ? '#eff6ff' : 'transparent',
                            color: isNotesOpen ? '#1e40af' : '#0f172a',
                            border: '1px solid',
                            borderColor: isNotesOpen ? '#bfdbfe' : 'rgba(226, 232, 240, 0.9)',
                            '&:hover': {
                                bgcolor: '#f1f5f9',
                            },
                            transition: theme.transitions.create(['background-color', 'border-color'], {
                                easing: theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.enteringScreen,
                            }),
                        }}
                    >
                        <NoteIcon sx={{ color: isNotesOpen ? '#1e40af' : '#64748b' }} />
                        {!isCollapsed && (
                            <>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                                    Action Items
                                </Typography>

                                {/* Only show chips when Notes panel is open AND in list view */}
                                {showActionItemChips ? (
                                    <Box
                                        sx={{
                                            ml: 'auto',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            flexShrink: 0,
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <Tooltip
                                            title={hasDateRange ? `All action items in ${actionItemsRangeLabel}` : 'Select a date range to filter action items'}
                                            arrow
                                        >
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                label="All"
                                                disabled={!hasDateRange}
                                                onClick={() => openActionItemsWithStatus('')}
                                                aria-label={`All action items for ${actionItemsRangeLabel}`}
                                                sx={{
                                                    height: 20,
                                                    fontSize: '0.72rem',
                                                    fontWeight: 800,
                                                    '& .MuiChip-label': { px: 0.75 },
                                                }}
                                            />
                                        </Tooltip>

                                        <Tooltip
                                            title={hasDateRange ? `Open action items in ${actionItemsRangeLabel}` : 'Select a date range to filter action items'}
                                            arrow
                                        >
                                            <Chip
                                                size="small"
                                                color="success"
                                                label="Open"
                                                disabled={!hasDateRange}
                                                onClick={() => openActionItemsWithStatus('open')}
                                                aria-label={`Open action items for ${actionItemsRangeLabel}`}
                                                sx={{
                                                    height: 20,
                                                    fontSize: '0.72rem',
                                                    fontWeight: 800,
                                                    '& .MuiChip-label': { px: 0.75 },
                                                }}
                                            />
                                        </Tooltip>

                                        <Tooltip
                                            title={hasDateRange ? `Closed action items in ${actionItemsRangeLabel}` : 'Select a date range to filter action items'}
                                            arrow
                                        >
                                            <Chip
                                                size="small"
                                                label="Closed"
                                                disabled={!hasDateRange}
                                                onClick={() => openActionItemsWithStatus('closed')}
                                                aria-label={`Closed action items for ${actionItemsRangeLabel}`}
                                                sx={{
                                                    height: 20,
                                                    fontSize: '0.72rem',
                                                    fontWeight: 800,
                                                    bgcolor: 'grey.600',
                                                    color: '#fff',
                                                    '& .MuiChip-label': { px: 0.75 },
                                                    '&:hover': { bgcolor: 'grey.700' },
                                                    '&.Mui-disabled': {
                                                        bgcolor: 'grey.300',
                                                        color: 'grey.600',
                                                    },
                                                }}
                                            />
                                        </Tooltip>
                                    </Box>
                                ) : null}
                            </>
                        )}
                    </Box>
                </Tooltip>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Main Content Area (Navigation or Notes) */}
            <Box
                sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                {isNotesOpen ? (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: '#fff' }}>
                        <NotesPanel
                            module="delivery"
                            page={notesPage}
                            startDate={startDate}
                            endDate={endDate}
                            permissions={notesPermissions}
                            statusFilter={notesStatusFilter}
                            onClose={closeNotes}
                            onViewChange={setNotesView}
                        />
                    </Box>
                ) : (
                    <List
                        sx={{
                            flexGrow: 1,
                            px: 2,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            // Custom scrollbar styles for navigation list
                            '&::-webkit-scrollbar': {
                                width: '5px',
                            },
                            '&::-webkit-scrollbar-track': {
                                backgroundColor: 'transparent',
                                margin: '8px 0',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: '#cbd5e1',
                                borderRadius: '10px',
                                border: '1px solid transparent',
                                backgroundClip: 'padding-box',
                                '&:hover': {
                                    backgroundColor: '#1e40af',
                                },
                            },
                            // Firefox scrollbar
                            scrollbarWidth: 'thin',
                            scrollbarColor: '#cbd5e1 transparent',
                        }}
                    >
                        {visibleNavigationItems.map((item) => {
                            const Icon = item.icon;
                            const hasChildren = !!item.children && item.children.length > 0;
                            const isChildActive = hasChildren ? item.children!.some((c) => isChildPathActive(location.pathname, c.path)) : false;
                            const isActive = (item.path ? location.pathname === item.path : false) || isChildActive;

                            return (
                                <Box key={item.label} sx={{ mb: 1 }}>
                                    <ListItem disablePadding sx={{ display: 'block' }}>
                                        <Tooltip title={isCollapsed ? item.label : ""} placement="right" arrow>
                                            <ListItemButton
                                                onClick={() => handleItemClick(item)}
                                                selected={isActive}
                                                sx={{
                                                    borderRadius: 2,
                                                    minHeight: 48,
                                                    justifyContent: isCollapsed ? 'center' : 'initial',
                                                    px: 2.5,
                                                    py: 1.5,
                                                    '&.Mui-selected': {
                                                        backgroundColor: '#eff6ff', // Blue 50
                                                        color: '#1e40af',
                                                        '&:hover': {
                                                            backgroundColor: '#dbeafe', // Blue 100
                                                        },
                                                        '& .MuiListItemIcon-root': {
                                                            color: '#1e40af',
                                                        },
                                                    },
                                                    '&:hover': {
                                                        backgroundColor: '#f1f5f9', // Slate 100
                                                    },
                                                    transition: theme.transitions.create('justify-content', {
                                                        easing: theme.transitions.easing.sharp,
                                                        duration: theme.transitions.duration.enteringScreen,
                                                    }),
                                                }}
                                            >
                                                <ListItemIcon sx={{
                                                    minWidth: 0,
                                                    mr: isCollapsed ? 0 : 2,
                                                    color: isActive ? '#1e40af' : '#64748b',
                                                    justifyContent: 'center',
                                                    transition: theme.transitions.create('margin', {
                                                        easing: theme.transitions.easing.sharp,
                                                        duration: theme.transitions.duration.enteringScreen,
                                                    }),
                                                }}>
                                                    <Icon />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={item.label}
                                                    primaryTypographyProps={{
                                                        fontSize: '0.9rem',
                                                        fontWeight: isActive ? 600 : 500,
                                                    }}
                                                    sx={{
                                                        opacity: isCollapsed ? 0 : 1,
                                                        display: isCollapsed ? 'none' : 'block',
                                                        transition: theme.transitions.create('opacity', {
                                                            easing: theme.transitions.easing.sharp,
                                                            duration: theme.transitions.duration.enteringScreen,
                                                        }),
                                                    }}
                                                />
                                                {hasChildren && !isCollapsed ? (openSubmenus[item.label] ? <ExpandLess /> : <ExpandMore />) : null}
                                            </ListItemButton>
                                        </Tooltip>
                                    </ListItem>

                                    {/* Submenu: Only show when not collapsed (or if expanded) */}
                                    {hasChildren && !isCollapsed && (
                                        <Collapse in={!!openSubmenus[item.label]} timeout="auto" unmountOnExit>
                                            <List disablePadding sx={{ pl: 2 }}>
                                                {item.children!.map((child) => {
                                                    const childActive = isChildPathActive(location.pathname, child.path);
                                                    return (
                                                        <ListItem key={child.path} disablePadding sx={{ mb: 0.5 }}>
                                                            <ListItemButton
                                                                onClick={() => navigate(child.path)}
                                                                selected={childActive}
                                                                sx={{
                                                                    borderRadius: 2,
                                                                    py: 1.1,
                                                                    pl: 3,
                                                                    '&.Mui-selected': {
                                                                        backgroundColor: '#eff6ff',
                                                                        color: '#1e40af',
                                                                    },
                                                                    '&:hover': {
                                                                        backgroundColor: '#f1f5f9',
                                                                    },
                                                                }}
                                                            >
                                                                <ListItemText
                                                                    primary={child.label}
                                                                    primaryTypographyProps={{
                                                                        fontSize: '0.85rem',
                                                                        fontWeight: childActive ? 600 : 500,
                                                                        color: childActive ? '#1e40af' : '#475569',
                                                                    }}
                                                                />
                                                            </ListItemButton>
                                                        </ListItem>
                                                    );
                                                })}
                                            </List>
                                        </Collapse>
                                    )}
                                </Box>
                            );
                        })}
                    </List>
                )}
            </Box>

            {/* User Profile Section (Bottom) */}
            <Divider />
            <Box sx={{ p: isCollapsed ? 1 : 2, display: isNotesOpen ? 'none' : 'block' }}>
                <Tooltip title={isCollapsed ? userName : ""} placement="right" arrow>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            p: 1.5,
                            borderRadius: 2,
                            cursor: 'pointer',
                            justifyContent: isCollapsed ? 'center' : 'flex-start',
                            '&:hover': { bgcolor: '#f1f5f9' },
                            transition: theme.transitions.create(['padding', 'justify-content'], {
                                easing: theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.enteringScreen,
                            }),
                        }}
                        onClick={handleMenuOpen}
                    >
                        <Avatar
                            sx={{
                                width: 36,
                                height: 36,
                                minWidth: 36,
                                bgcolor: '#1e40af',
                                fontSize: '0.875rem',
                                fontWeight: 600,
                            }}
                        >
                            {userInitials}
                        </Avatar>
                        <Box sx={{
                            minWidth: 0,
                            flex: 1,
                            opacity: isCollapsed ? 0 : 1,
                            width: isCollapsed ? 0 : 'auto',
                            overflow: 'hidden',
                            display: isCollapsed ? 'none' : 'block',
                            transition: theme.transitions.create('opacity', {
                                easing: theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.enteringScreen,
                            }),
                        }}>
                            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600, color: '#0f172a' }}>
                                {userName}
                            </Typography>
                            <Typography variant="caption" noWrap sx={{ color: '#64748b', display: 'block' }}>
                                {userEmail}
                            </Typography>
                        </Box>
                    </Box>
                </Tooltip>
            </Box>

            {/* User Menu Popup */}
            <Menu
                anchorEl={anchorEl}
                id="account-menu"
                open={open}
                onClose={handleMenuClose}
                onClick={handleMenuClose}
                PaperProps={{
                    elevation: 4,
                    sx: {
                        overflow: 'visible',
                        mt: -1, // Adjust based on anchor
                        ml: 2,
                        minWidth: 200,
                        borderRadius: 2,
                    },
                }}
                transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                    <ListItemIcon>
                        <Logout fontSize="small" sx={{ color: 'error.main' }} />
                    </ListItemIcon>
                    Sign out
                </MenuItem>
            </Menu>

        </Box>
    );

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />

            {/* Top AppBar - simplified (mostly for title on mobile or page actions) */}
            <AppBar
                position="fixed"
                sx={{
                    width: { md: `calc(100% - ${(isNotesOpen ? NOTES_PANEL_WIDTH : (isCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH))}px)` },
                    ml: { md: `${(isNotesOpen ? NOTES_PANEL_WIDTH : (isCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH))}px` },
                    boxShadow: 'none',
                    backgroundColor: 'transparent',
                    pointerEvents: 'none', // Let clicks pass through if empty, but we have buttons
                    transition: theme.transitions.create(['width', 'margin'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                }}
            >
                {/* Important: keep the fixed toolbar from blocking clicks on underlying content.
                    Only the actual interactive elements inside should receive pointer events. */}
                <Toolbar sx={{ pointerEvents: 'none', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', pointerEvents: 'auto' }}>
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            edge="start"
                            onClick={onMenuClick} // from props (handleDrawerToggle)
                            sx={{ mr: 2, display: { md: 'none' }, color: '#1e40af' }}
                        >
                            <MenuIcon />
                        </IconButton>
                        {/* Page Title shown in header if desired */}
                        <Typography variant="h6" noWrap component="div" sx={{ color: '#0f172a', fontWeight: 600 }}>
                            {pageTitle}
                        </Typography>
                    </Box>

                    {/* Page Actions */}
                    <Box sx={{ pointerEvents: 'auto' }}>
                        {pageActions}
                    </Box>
                </Toolbar>
            </AppBar>

            {/* Navigation Drawer */}
            <Box
                component="nav"
                sx={{
                    width: { md: isNotesOpen ? NOTES_PANEL_WIDTH : (isCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH) },
                    flexShrink: { md: 0 },
                    transition: theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                }}
                aria-label="mailbox folders"
            >
                {/* Mobile Temporary Drawer */}
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={onMenuClick}
                    ModalProps={{
                        keepMounted: true, // Better open performance on mobile.
                    }}
                    sx={{
                        display: { xs: 'block', md: 'none' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: isNotesOpen ? NOTES_PANEL_WIDTH : DRAWER_WIDTH,
                            borderRight: '1px solid #e2e8f0',
                            // Custom scrollbar for drawer paper
                            '&::-webkit-scrollbar': {
                                width: '6px',
                            },
                            '&::-webkit-scrollbar-track': {
                                backgroundColor: '#f8fafc',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: '#cbd5e1',
                                borderRadius: '3px',
                                '&:hover': {
                                    backgroundColor: '#1e40af',
                                },
                            },
                        },
                    }}
                >
                    {drawerContent}
                </Drawer>

                {/* Desktop Permanent Drawer */}
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: isNotesOpen ? NOTES_PANEL_WIDTH : (isCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH),
                            borderRight: '1px solid #e2e8f0',
                            backgroundColor: isCollapsed ? '#f8fafc' : '#ffffff',
                            overflowX: 'hidden', // Hide horizontal scroll during transition
                            transition: theme.transitions.create('width', {
                                easing: theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.enteringScreen,
                            }),
                            // Custom scrollbar for drawer paper
                            '&::-webkit-scrollbar': {
                                width: '6px',
                            },
                            '&::-webkit-scrollbar-track': {
                                backgroundColor: '#f8fafc',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: '#cbd5e1',
                                borderRadius: '3px',
                                '&:hover': {
                                    backgroundColor: '#1e40af',
                                },
                            },
                        },
                    }}
                    open
                >
                    {drawerContent}
                </Drawer>
            </Box>
        </Box>
    );
};

export default Sidebar;
